import { ReactNode, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'

import {
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Typography,
    makeStyles,
    Box,
    Button,
    LinearProgress
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import BackIcon from '@material-ui/icons/ArrowBackIos'

import { PaymentErrorDialog } from 'UI/Components'

import { SetupIntentResult } from '@stripe/stripe-js'

import { SelectPlan } from './SelectPlan'
import { SelectSeats } from './SelectSeats'
import { Payment } from './Payment'
import { SCREEN } from './helpers'

import { filterURLParams, logToAnalytics } from 'modules'
import { usePayment, useBoolean, useStigg, useQueryParams, useServiceUsage } from 'hooks'
import { BillingPeriod } from '@stigg/js-client-sdk'

const useStyles = makeStyles(theme => ({
    title: {
        fontSize: '16px',
        textAlign: 'center',
        fontWeight: theme.fontWeightBold
    },
    actions: {
        padding: theme.spacing(3),
        justifyContent: 'center'
    },
    content: {
        paddingBottom: '48px'
    }
}))

type Props = {
    onClose: () => void
}

export const UpgradePlanDialog = ({ onClose }: Props) => {
    const classes = useStyles()

    const params = useQueryParams()
    const initialStep =
        Number(params.get('upgradePlanStep')) === 1 ? SCREEN.SelectSeats : SCREEN.SelectPlan
    const [step, setStep] = useState(initialStep)
    const history = useHistory()

    const { usedUsersSeats, userSeatsQuota, billingPeriod, isTrialPlan } = useServiceUsage()

    const [errorMessage, setErrorMessage] = useState('')

    const paymentError = useBoolean()
    const trialDialog = useBoolean()
    const isLoading = useBoolean()

    const {
        bucket,
        plans,
        stiggLoading,
        updateBucket,
        availablePlanIds,
        revalidateStiggPaywall,
        brandKit,
        trial
    } = useStigg()

    const { period, planId, totalLicenses } = bucket

    const {
        startCheckout,
        updatePlan,
        startTrial,
        submitStripeForm,
        clientSecret,
        stripePromise,
        estimateSubscriptionData,
        estimateSubscription,
        isEstimationLoading,
        isLoading: isPaymentLoading,
        fetchUsersToDowngrade,
        usersToDowngrade,
        applyCoupon,
        couponResData,
        isCouponApplying
    } = usePayment({ totalLicenses })

    const selectedPlan = plans?.[bucket.planId]

    const handleError = (res: SetupIntentResult) => {
        setErrorMessage(res?.error?.message || '')
        paymentError.setTrue()
    }

    const initialUserSeatsToBuy = Math.max(usedUsersSeats, userSeatsQuota)

    useEffect(() => {
        if (Number(params.get('upgradePlanStep')) !== 1) return

        const planId = params.get('planId') || brandKit?.planId
        const period =
            params.get('billingPeriod') || brandKit?.billingPeriod || BillingPeriod.Annually

        if (!planId) return

        history.replace(
            filterURLParams(history.location, ['upgradePlanStep', 'planId', 'billingPeriod'])
        )
        updateBucket({
            planId,
            period,
            totalLicenses: initialUserSeatsToBuy
        })
    }, [
        updateBucket,
        params,
        period,
        planId,
        usedUsersSeats,
        userSeatsQuota,
        initialUserSeatsToBuy,
        brandKit,
        history
    ])

    useEffect(() => {
        revalidateStiggPaywall()
    }, [revalidateStiggPaywall])

    return (
        <>
            <PaymentErrorDialog
                onClose={paymentError.setFalse}
                isOpen={paymentError.isTrue}
                onClick={paymentError.setFalse}
                errorMessage={errorMessage}
            />

            <Dialog open onClose={onClose} fullWidth maxWidth="lg">
                {(isPaymentLoading || stiggLoading) && <LinearProgress />}
                <DialogTitle
                    style={{
                        position: 'relative'
                    }}
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                        {step !== SCREEN.SelectPlan ? (
                            <Button
                                variant="text"
                                color="default"
                                startIcon={<BackIcon fontSize="small" />}
                                onClick={() => setStep(step => step - 1)}
                            >
                                {step === SCREEN.SelectSeats ? 'Change plan' : 'Change seats'}
                            </Button>
                        ) : (
                            <Box />
                        )}
                        <Typography
                            data-cy="upgrade-plan-dialog-title"
                            className={classes.title}
                            style={{
                                position: 'absolute',
                                left: '50%',
                                top: '50%',
                                transform: 'translate3d(-50%, -50%, 0)'
                            }}
                        >
                            Upgrade plan
                        </Typography>
                        <IconButton onClick={onClose}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent className={classes.content}>
                    {!stiggLoading && (
                        <>
                            <TabPanel value={step} index={SCREEN.SelectPlan}>
                                {plans && (
                                    <SelectPlan
                                        availablePlanIds={availablePlanIds}
                                        setStep={setStep}
                                        updateBucket={updateBucket}
                                        isLoading={isPaymentLoading}
                                        plans={plans}
                                        trial={trial}
                                        updatePlan={updatePlan}
                                        startTrial={planId => {
                                            isLoading.setTrue()

                                            startTrial(planId).then(() => {
                                                logToAnalytics('send_request_for_starting_trial', {
                                                    source: 'upgrade_plan_dialog'
                                                })
                                                onClose()
                                                trialDialog.setTrue()
                                            })
                                        }}
                                    />
                                )}
                            </TabPanel>
                            <TabPanel value={step} index={SCREEN.SelectSeats}>
                                <SelectSeats
                                    setStep={setStep}
                                    updateBucket={updateBucket}
                                    startCheckout={startCheckout}
                                    initialUserSeatsToBuy={initialUserSeatsToBuy}
                                    bucket={bucket}
                                    plan={selectedPlan}
                                    activePlanPeriod={billingPeriod}
                                    userSeatsQuota={userSeatsQuota}
                                    isTrialPlan={isTrialPlan}
                                    usedUsersSeats={usedUsersSeats}
                                    fetchUsersToDowngrade={fetchUsersToDowngrade}
                                    usersToDowngrade={usersToDowngrade}
                                />
                            </TabPanel>
                            <TabPanel value={step} index={SCREEN.PaymentConfirmation}>
                                <Payment
                                    applyCoupon={applyCoupon}
                                    couponResData={couponResData}
                                    isCouponApplying={isCouponApplying}
                                    bucket={bucket}
                                    setStep={setStep}
                                    stripePromise={stripePromise}
                                    confirmPlanUpdate={() => {
                                        // if user added extra seats to basic plan we add object to addons otherwise it must be empty

                                        updatePlan({
                                            planId,
                                            unitQuantity: totalLicenses,
                                            billingPeriod: period
                                        })
                                            .then(onClose)
                                            .catch(paymentError.setTrue)
                                    }}
                                    clientSecret={clientSecret}
                                    isLoading={isPaymentLoading}
                                    submitStripeForm={submitStripeForm}
                                    selectedPlan={selectedPlan}
                                    onError={handleError}
                                    estimateSubscriptionData={estimateSubscriptionData}
                                    estimateSubscription={estimateSubscription}
                                    isEstimationLoading={isEstimationLoading}
                                />
                            </TabPanel>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    )
}

type PanelProps = {
    children: ReactNode
    value: number
    index: number
}

const TabPanel = ({ children, value, index }: PanelProps) => {
    return <Box hidden={value !== index}>{value === index && children}</Box>
}
